import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaRegEye } from "react-icons/fa";
import AdminShell from "../../Components/admin/AdminShell";
import CoverImageUpload from "../../Components/admin/CoverImageUpload";
import GalleryImageUpload from "../../Components/admin/GalleryImageUpload";
import { createEventMultipart, EventApiError } from "../../services/eventsApi";
import { getApiBase } from "../../services/apiConfig";
import { getAdminJwt } from "../../services/authToken";
import "../../Components/admin/adminCreateArticle.css";

function localDateISO(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_FILES = 20;

function validateImageFiles(coverFile, galleryFiles) {
  const all = [...(coverFile ? [coverFile] : []), ...(galleryFiles || [])];
  if (all.length > MAX_TOTAL_FILES) {
    return `Too many files (max ${MAX_TOTAL_FILES} including cover).`;
  }
  for (let i = 0; i < all.length; i += 1) {
    if (all[i].size > MAX_IMAGE_BYTES) {
      return `Each image must be at most 10 MB (${all[i].name}).`;
    }
  }
  return null;
}

function validatePublishPayload({ title, content, author, category, publishDate, coverFile }) {
  if (!String(title || "").trim()) return "Title is required to publish.";
  if (!String(content || "").trim()) return "Content is required to publish.";
  if (author !== "hassan") return "Author must be Getty Denkyi to publish.";
  if (!["news", "ngo", "updates"].includes(category)) return "Category is required to publish.";
  if (!String(publishDate || "").trim()) return "Publish date is required to publish.";
  if (!(coverFile instanceof File)) return "Cover image is required to publish.";
  return null;
}

export default function CreateArticle() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [author, setAuthor] = useState("");
  const [category, setCategory] = useState("");
  const [publishDate, setPublishDate] = useState(() => localDateISO());
  const [coverFile, setCoverFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const galleryMax = MAX_TOTAL_FILES - (coverFile ? 1 : 0);

  const submit = async () => {
    setError("");
    setSuccess("");

    if (!getApiBase()) {
      setError("Events API is unavailable. Check the server or set REACT_APP_API_BASE.");
      return;
    }
    if (!getAdminJwt()) {
      setError("You need to sign in. Your session may have expired (tokens last about 1 hour).");
      return;
    }

    const publishDateToSend = localDateISO();
    setPublishDate(publishDateToSend);

    const pubErr = validatePublishPayload({
      title,
      content,
      author,
      category,
      publishDate: publishDateToSend,
      coverFile,
    });
    if (pubErr) {
      setError(pubErr);
      return;
    }

    const fileErr = validateImageFiles(coverFile, galleryFiles);
    if (fileErr) {
      setError(fileErr);
      return;
    }

    setSubmitting(true);
    try {
      await createEventMultipart({
        title,
        excerpt,
        content,
        author,
        category,
        publishDate: publishDateToSend && String(publishDateToSend).trim() ? publishDateToSend : "",
        status: "published",
        coverFile: coverFile || undefined,
        galleryFiles,
      });
      setSuccess("Event published.");
      setTimeout(() => navigate("/admin", { replace: false }), 600);
    } catch (e) {
      if (e instanceof EventApiError) {
        const parts = [e.message, ...(e.errors || [])].filter(Boolean);
        setError(parts.join(" "));
      } else {
        setError(e?.message || "Request failed.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminShell
      title="Create new event"
      subtitle="Write and publish your event"
      actions={
        <div className="acaHeaderActions">
          <button type="button" className="acaBtn acaBtnPrimary" disabled={submitting} onClick={() => submit()}>
            <span className="acaBtnIcon" aria-hidden="true">
              <FaRegEye />
            </span>
            Publish
          </button>
        </div>
      }
    >
      {error ? (
        <div className="acaFormAlert acaFormAlertError" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="acaFormAlert acaFormAlertSuccess" role="status">
          {success}
        </div>
      ) : null}

      <div className="acaGrid">
        <div className="acaLeft">
          <div className="acaCard">
            <div className="acaCardHeader">Article Content</div>
            <div className="acaCardBody">
              <label className="acaLabel" htmlFor="acaTitle">
                Title
              </label>
              <input
                id="acaTitle"
                className="acaInput"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter article title..."
              />

              <label className="acaLabel" htmlFor="acaExcerpt" style={{ marginTop: 14 }}>
                Excerpt
              </label>
              <textarea
                id="acaExcerpt"
                className="acaTextarea"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="Brief description of the article..."
                rows={3}
              />

              <label className="acaLabel" htmlFor="acaContent" style={{ marginTop: 14 }}>
                Content
              </label>
              <textarea
                id="acaContent"
                className="acaTextarea acaTextareaTall"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your article content here..."
                rows={7}
              />
            </div>
          </div>

          <div className="acaCard" style={{ marginTop: 16 }}>
            <div className="acaCardHeader">Gallery images</div>
            <div className="acaCardBody">
              <GalleryImageUpload
                files={galleryFiles}
                onFilesChange={setGalleryFiles}
                inputId="acaGalleryImages"
                maxFiles={galleryMax}
              />
            </div>
          </div>
        </div>

        <div className="acaRight">
          <div className="acaCard">
            <div className="acaCardHeader">Publication</div>
            <div className="acaCardBody">
              <label className="acaLabel" htmlFor="acaAuthor">
                Author
              </label>
              <select id="acaAuthor" className="acaSelect" value={author} onChange={(e) => setAuthor(e.target.value)}>
                <option value="">Select author</option>
                <option value="hassan">Getty Denkyi</option>
              </select>

              <label className="acaLabel" htmlFor="acaCategory" style={{ marginTop: 12 }}>
                Category
              </label>
              <select id="acaCategory" className="acaSelect" value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">Select category</option>
                <option value="news">News</option>
                <option value="ngo">NGO</option>
                <option value="updates">Updates</option>
              </select>

              <label className="acaLabel" htmlFor="acaDate" style={{ marginTop: 12 }}>
                Publish Date
              </label>
              <input
                id="acaDate"
                className="acaInput"
                type="date"
                value={publishDate}
                onChange={(e) => setPublishDate(e.target.value)}
              />
            </div>
          </div>

          <div className="acaCard" style={{ marginTop: 16 }}>
            <div className="acaCardHeader">Cover image</div>
            <div className="acaCardBody">
              <CoverImageUpload file={coverFile} onFileChange={setCoverFile} inputId="acaCoverImage" />
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
